﻿import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useWatch } from 'react-hook-form'
import type { Control } from 'react-hook-form'

import { useUnsavedChangesGuard } from '#/shared/lib/form'

import { SHIPMENT_STAGES, useRouteGeocode } from '#/entities/shipment'
import type { ShipmentStage } from '#/entities/shipment'
import { GetActiveLoadboardsApiV1LoadboardsActiveGet200Item } from '#/shared/api/generated/loadboard/models'
import { useGetActiveLoadboardsApiV1LoadboardsActiveGet } from '#/shared/api/generated/loadboard/theCargoLoadboard'
import type { LoadboardChoice } from '#/shared/api/generated/shipment/models'
import { FormType } from '#/shared/api/generated/shipment/models'
import { useDebouncedValue } from '#/shared/lib/hooks'

import {
  CustomerCard,
  LocationCard,
  PaymentCard,
  ShipmentCard,
  VehiclesCard,
  useCustomerForm,
  useLocationFlags,
  useLocationForm,
  usePaymentForm,
  useSameAsCustomer,
  useShipmentForm,
  useVehiclesForm,
} from '#/features/order-create'
import type {
  CustomerFormData,
  CustomerSavedField,
  LocationData,
  LocationFormData,
  PaymentFormData,
  ShipmentFormData,
  UseLocationFlagsResult,
  VehiclesFormData,
} from '#/features/order-create'

import {
  MANAGE_FIELD_SCOPE,
  ManageFieldsProvider,
  getHiddenFields,
  useFormPreferenceSync,
} from '#/shared/lib/manage-fields'
import type { ManageFieldScope } from '#/shared/lib/manage-fields'

import { useDevSample } from '../model/use-dev-sample'
import { usePriceCheck } from '../model/use-price-check'
import { useSaveNewTags } from '../model/use-save-new-tags'
import { useSaveNewTerminals } from '../model/use-save-new-terminals'
import { stripHiddenFields } from '../lib/strip-hidden-fields'
import { newOrderFormDefaults } from '../lib/to-form-defaults'
import { ManageFieldsPopover } from './manage-fields-popover'
import { QuoteShipmentCard } from './quote-shipment-card'
import { MarketRateCheck, MarketRateDialog } from '#/features/market-rate'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '#/shared/ui/accordion'
import { Button } from '#/shared/ui/button'
import { Checkbox } from '#/shared/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { FormCard } from '#/shared/ui/form-card'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { MapboxMap } from '#/shared/ui/mapbox-map'
import type { MapboxMapLocation } from '#/shared/ui/mapbox-map'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

export const ORDER_FORM_VARIANT = {
  create: 'create',
  edit: 'edit',
  dispatch: 'dispatch',
} as const

export type OrderFormVariant =
  (typeof ORDER_FORM_VARIANT)[keyof typeof ORDER_FORM_VARIANT]

export interface OrderFormPayload {
  stage: ShipmentStage
  customer: CustomerFormData
  customerDirty: boolean
  customerId?: string
  vehicles: VehiclesFormData
  location: LocationFormData
  shipment: ShipmentFormData
  payment: PaymentFormData
  isDirty: boolean
  postTo?: LoadboardChoice
}

const LB_TARGET_LABEL_KEY: Record<
  GetActiveLoadboardsApiV1LoadboardsActiveGet200Item,
  string
> = {
  [GetActiveLoadboardsApiV1LoadboardsActiveGet200Item.central]:
    'detailToolbar.postOptions.cd',
  [GetActiveLoadboardsApiV1LoadboardsActiveGet200Item.super]:
    'detailToolbar.postOptions.slb',
  [GetActiveLoadboardsApiV1LoadboardsActiveGet200Item.both]:
    'detailToolbar.postOptions.both',
}

const SAVE_AND_POST_LABEL_KEY: Record<
  GetActiveLoadboardsApiV1LoadboardsActiveGet200Item,
  string
> = {
  [GetActiveLoadboardsApiV1LoadboardsActiveGet200Item.central]:
    'orderCreate.page.saveAndPostOptions.cd',
  [GetActiveLoadboardsApiV1LoadboardsActiveGet200Item.super]:
    'orderCreate.page.saveAndPostOptions.slb',
  [GetActiveLoadboardsApiV1LoadboardsActiveGet200Item.both]:
    'orderCreate.page.saveAndPostOptions.both',
}

export interface OrderFormDefaults {
  customer?: Partial<CustomerFormData>
  vehicles?: Partial<VehiclesFormData>
  location?: Partial<LocationFormData>
  shipment?: Partial<ShipmentFormData>
  payment?: Partial<PaymentFormData>
  tagSeedOptions?: MultiSelectOption[]
}

export interface OrderFormProps {
  stage?: ShipmentStage
  variant?: OrderFormVariant
  defaultValues?: OrderFormDefaults
  isSubmitting?: boolean
  topSection?: ReactNode
  shipmentId?: string
  onCancel?: () => void
  onSubmit?: (data: OrderFormPayload) => void | Promise<void>
}

function locationFieldsToAddress(
  location: Partial<LocationData> | undefined,
): { address: string; label: string } | undefined {
  if (!location) return undefined
  const parts = [location.address, location.city, location.state, location.zip]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0)
  if (parts.length === 0) return undefined
  const address = parts.join(', ')
  const businessName = location.businessName?.trim()
  const label = businessName && businessName.length > 0 ? businessName : address
  return { address, label }
}

const MAP_DEBOUNCE_MS = 600

function PreviewRoadAccordion({
  control,
}: {
  control: Control<LocationFormData>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState('')
  const isOpen = open === 'preview-road'
  const origin = useWatch({ control, name: 'origin' })
  const destination = useWatch({ control, name: 'destination' })
  const liveOrigin = locationFieldsToAddress(origin)
  const liveDestination = locationFieldsToAddress(destination)
  const debouncedOriginAddress = useDebouncedValue(
    liveOrigin?.address,
    MAP_DEBOUNCE_MS,
  )
  const debouncedDestinationAddress = useDebouncedValue(
    liveDestination?.address,
    MAP_DEBOUNCE_MS,
  )
  const geocode = useRouteGeocode(
    debouncedOriginAddress,
    debouncedDestinationAddress,
    isOpen,
  )
  const mapOrigin = useMemo<MapboxMapLocation | undefined>(
    () =>
      geocode.origin && liveOrigin
        ? {
            label: liveOrigin.label,
            coords: [geocode.origin.lat, geocode.origin.lng],
          }
        : undefined,
    [geocode.origin, liveOrigin],
  )
  const mapDestination = useMemo<MapboxMapLocation | undefined>(
    () =>
      geocode.destination && liveDestination
        ? {
            label: liveDestination.label,
            coords: [geocode.destination.lat, geocode.destination.lng],
          }
        : undefined,
    [geocode.destination, liveDestination],
  )

  return (
    <Accordion
      type="single"
      collapsible
      value={open}
      onValueChange={setOpen}
      className="flex w-full flex-col"
    >
      <AccordionItem value="preview-road">
        <AccordionTrigger>{t('orderCreate.previewRoad')}</AccordionTrigger>
        <AccordionContent>
          {isOpen && (
            <MapboxMap
              origin={mapOrigin}
              destination={mapDestination}
              className="h-64"
              emptyMessage={t('quoteDetails.map.empty')}
            />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

function ToolbarDivider() {
  return <div className="h-8 w-px bg-gray-200" />
}

function isQuoteLikeStage(stage: ShipmentStage): boolean {
  return stage === SHIPMENT_STAGES.QUOTE || stage === SHIPMENT_STAGES.LEAD
}

function formTitleKey(stage: ShipmentStage, variant: OrderFormVariant): string {
  if (variant !== ORDER_FORM_VARIANT.edit) {
    return isQuoteLikeStage(stage)
      ? 'orderCreate.page.quoteTitle'
      : 'orderCreate.page.title'
  }
  if (stage === SHIPMENT_STAGES.LEAD) return 'orderCreate.page.editLeadTitle'
  if (stage === SHIPMENT_STAGES.QUOTE) return 'orderCreate.page.editQuoteTitle'
  return 'orderCreate.page.editTitle'
}

function manageFieldsScopeFor(stage: ShipmentStage): ManageFieldScope {
  return isQuoteLikeStage(stage)
    ? MANAGE_FIELD_SCOPE.quote
    : MANAGE_FIELD_SCOPE.order
}

function DevSampleToggle() {
  const { enabled, setEnabled } = useDevSample()
  return (
    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 select-none">
      <Checkbox
        checked={enabled}
        onCheckedChange={(value) => setEnabled(value === true)}
      />
      Sample data
    </label>
  )
}

function EditToolbar({
  stage,
  variant,
  onCancel,
  onSave,
  onSaveAndPost,
  isSubmitting,
}: {
  stage: ShipmentStage
  variant: OrderFormVariant
  onCancel?: () => void
  onSave: () => void
  onSaveAndPost: (target: LoadboardChoice) => void
  isSubmitting: boolean
}) {
  const { t } = useTranslation()
  const isOrder = stage === SHIPMENT_STAGES.ORDER
  const activeQuery = useGetActiveLoadboardsApiV1LoadboardsActiveGet({
    query: { enabled: isOrder },
  })
  const targets = isOrder ? (activeQuery.data?.data ?? []) : []
  const titleKey = formTitleKey(stage, variant)
  return (
    <div className="flex h-12 items-center justify-between">
      <div className="flex items-center gap-5">
        <span className="text-sm font-medium text-gray-500">{t(titleKey)}</span>
        <ToolbarDivider />
        <ManageFieldsPopover scope={manageFieldsScopeFor(stage)} />
        {import.meta.env.DEV && <DevSampleToggle />}
      </div>
      <div className="flex h-8 items-center gap-5">
        {targets.length === 1 && (
          <>
            <Button
              type="button"
              variant="outline"
              size="xs"
              pressEffect
              className="px-2.5 select-none"
              onClick={() =>
                onSaveAndPost(targets[0] as unknown as LoadboardChoice)
              }
              disabled={isSubmitting}
            >
              {t(SAVE_AND_POST_LABEL_KEY[targets[0]])}
            </Button>
            <ToolbarDivider />
          </>
        )}
        {targets.length > 1 && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  pressEffect
                  onMouseDown={(e) => {
                    if (e.detail > 1) e.preventDefault()
                  }}
                  className="gap-2.5 px-2.5 select-none focus-visible:focus-ring data-[state=open]:focus-ring"
                >
                  {t('orderCreate.page.saveAndPost')}
                  <ChevronDownIcon className="size-2.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="center"
                className="min-w-[12rem]"
                onCloseAutoFocus={() =>
                  window.getSelection()?.removeAllRanges()
                }
              >
                {targets.map((target) => (
                  <DropdownMenuItem
                    key={target}
                    onSelect={() =>
                      onSaveAndPost(target as unknown as LoadboardChoice)
                    }
                  >
                    {t(LB_TARGET_LABEL_KEY[target])}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <ToolbarDivider />
          </>
        )}
        <div className="flex items-center gap-2.5">
          <Button
            type="button"
            variant="outline"
            size="xs"
            pressEffect
            className="px-2.5"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('orderCreate.page.cancel')}
          </Button>
          <Button
            type="button"
            size="xs"
            pressEffect
            className="px-3.75"
            onClick={onSave}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('orderCreate.page.saving')
              : t('orderCreate.page.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function DispatchToolbar({
  onCancel,
  onSave,
  isSubmitting,
}: {
  onCancel?: () => void
  onSave: () => void
  isSubmitting: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-12 items-center justify-between">
      <div className="flex items-center gap-5">
        <span className="text-sm font-medium text-gray-500">
          {t('orderCreate.page.dispatchTitle')}
        </span>
        <ToolbarDivider />
        <ManageFieldsPopover scope={MANAGE_FIELD_SCOPE.order} />
      </div>
      <div className="flex h-8 items-center gap-2.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          pressEffect
          className="px-2.5"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('orderCreate.page.cancel')}
        </Button>
        <Button
          type="button"
          size="xs"
          pressEffect
          className="px-3.75"
          onClick={onSave}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? t('orderCreate.page.saving')
            : t('orderCreate.page.dispatchNow')}
        </Button>
      </div>
    </div>
  )
}

function FormToolbar({
  stage,
  variant,
  onCancel,
  onSave,
  onSaveAndPost,
  isSubmitting,
}: {
  stage: ShipmentStage
  variant: OrderFormVariant
  onCancel?: () => void
  onSave: () => void
  onSaveAndPost: (target: LoadboardChoice) => void
  isSubmitting: boolean
}) {
  if (variant === ORDER_FORM_VARIANT.dispatch) {
    return (
      <DispatchToolbar
        onCancel={onCancel}
        onSave={onSave}
        isSubmitting={isSubmitting}
      />
    )
  }
  return (
    <EditToolbar
      stage={stage}
      variant={variant}
      onCancel={onCancel}
      onSave={onSave}
      onSaveAndPost={onSaveAndPost}
      isSubmitting={isSubmitting}
    />
  )
}

type OrderForms = {
  customer: ReturnType<typeof useCustomerForm>
  shipment: ReturnType<typeof useShipmentForm>
  payment: ReturnType<typeof usePaymentForm>
  vehicles: ReturnType<typeof useVehiclesForm>
  locations: ReturnType<typeof useLocationForm>
}

function useOrderForms(
  defaults: OrderFormDefaults | undefined,
  requireDates: boolean | undefined,
): OrderForms {
  const effective = useMemo(
    () => defaults ?? newOrderFormDefaults(),
    [defaults],
  )
  return {
    customer: useCustomerForm({ defaultValues: effective.customer }),
    shipment: useShipmentForm({
      defaultValues: effective.shipment,
      requireDates,
    }),
    payment: usePaymentForm({ defaultValues: effective.payment }),
    vehicles: useVehiclesForm({ defaultValues: effective.vehicles }),
    locations: useLocationForm({ defaultValues: effective.location }),
  }
}

async function validateAndCollect(
  forms: OrderForms,
  customerId: string | undefined,
  stage: ShipmentStage,
): Promise<OrderFormPayload | undefined> {
  const { customer, vehicles, locations, shipment, payment } = forms
  const [cOk, vOk, lOk, sOk, pOk] = await Promise.all([
    customer.trigger(),
    vehicles.trigger(),
    locations.trigger(),
    shipment.trigger(),
    payment.trigger(),
  ])
  if (!cOk || !vOk || !lOk || !sOk || !pOk) return undefined
  const isDirty =
    customer.isDirty ||
    vehicles.isDirty ||
    locations.isDirty ||
    shipment.isDirty ||
    payment.isDirty
  return {
    stage,
    customer: customer.getValues(),
    customerDirty: customer.isDirty,
    customerId,
    vehicles: vehicles.getValues(),
    location: locations.getValues(),
    shipment: shipment.getValues(),
    payment: payment.getValues(),
    isDirty,
  }
}

function focusFirstInvalidField(): void {
  const el = document.querySelector<HTMLElement>('[aria-invalid="true"]')
  if (!el) return
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  if (typeof el.focus === 'function') el.focus({ preventScroll: true })
}

function VehicleSectionCard({ form }: { form: OrderForms['vehicles'] }) {
  const { t } = useTranslation()
  return (
    <FormCard title={t('orderCreate.vehicles.title')} contentClassName="px-2.5">
      <VehiclesCard
        bare
        hideTitle
        control={form.control}
        register={form.register}
        setValue={form.setValue}
        errors={form.errors}
        fields={form.fields}
        onAdd={form.addVehicle}
        onRemove={form.removeVehicle}
      />
    </FormCard>
  )
}

function RouteSectionCard({
  form,
  onSameAsCustomer,
  flags,
}: {
  form: OrderForms['locations']
  onSameAsCustomer?: ReturnType<typeof useSameAsCustomer>
  flags: UseLocationFlagsResult
}) {
  const { t } = useTranslation()
  return (
    <FormCard
      title={t('orderCreate.location.routeTitle')}
      contentClassName="px-2.5"
    >
      <LocationCard
        bare
        control={form.control}
        register={form.register}
        setValue={form.setValue}
        clearErrors={form.clearErrors}
        errors={form.errors}
        onSameAsCustomer={onSameAsCustomer}
        flags={flags}
      />
    </FormCard>
  )
}

function QuoteRouteSectionCard({ form }: { form: OrderForms['locations'] }) {
  const { t } = useTranslation()
  return (
    <FormCard
      title={t('orderCreate.location.routeTitle')}
      contentClassName="px-2.5"
    >
      <LocationCard
        bare
        cityZipTypeOnly
        control={form.control}
        register={form.register}
        setValue={form.setValue}
        clearErrors={form.clearErrors}
        errors={form.errors}
      />
    </FormCard>
  )
}

function OrderStageColumns({
  forms,
  onSameAsCustomer,
  onCustomerSelect,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
  onPriceCheck,
  rateCheck,
  tagSeedOptions,
  flags,
}: {
  forms: OrderForms
  onSameAsCustomer?: ReturnType<typeof useSameAsCustomer>
  onCustomerSelect: (customerId: string) => void
  customerSavedField: CustomerSavedField | null
  onSaveAsNewCustomer: (field: CustomerSavedField) => void
  onClearNewCustomer: () => void
  onPriceCheck?: () => void
  rateCheck?: ReactNode
  tagSeedOptions?: MultiSelectOption[]
  flags: UseLocationFlagsResult
}) {
  const { customer, shipment, payment, vehicles, locations } = forms
  return (
    <div className="grid w-full grid-cols-1 gap-5 lg:grid-cols-2">
      <aside className="flex min-w-0 flex-col gap-5">
        <VehicleSectionCard form={vehicles} />
        <RouteSectionCard
          form={locations}
          onSameAsCustomer={onSameAsCustomer}
          flags={flags}
        />
      </aside>
      <main className="flex min-w-0 flex-col gap-5">
        <CustomerCard
          control={customer.control}
          register={customer.register}
          setValue={customer.setValue}
          errors={customer.errors}
          searchPlaceholders
          onCustomerSelect={(picked) => onCustomerSelect(picked.id)}
          customerSavedField={customerSavedField}
          onSaveAsNewCustomer={onSaveAsNewCustomer}
          onClearNewCustomer={onClearNewCustomer}
        />
        <ShipmentCard
          control={shipment.control}
          register={shipment.register}
          errors={shipment.errors}
          tagSeedOptions={tagSeedOptions}
        />
        <PaymentCard
          control={payment.control}
          register={payment.register}
          getValues={payment.getValues}
          setValue={payment.setValue}
          errors={payment.errors}
          formula={payment.formula}
          selectValues={payment.selectValues}
          onCarrierFeeChange={payment.onCarrierFeeChange}
          onPriceCheck={onPriceCheck}
          rateCheck={rateCheck}
        />
        <PreviewRoadAccordion control={locations.control} />
      </main>
    </div>
  )
}

function QuoteStageColumns({
  forms,
  onCustomerSelect,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
  onPriceCheck,
  rateCheck,
}: {
  forms: OrderForms
  onCustomerSelect: (customerId: string) => void
  customerSavedField: CustomerSavedField | null
  onSaveAsNewCustomer: (field: CustomerSavedField) => void
  onClearNewCustomer: () => void
  onPriceCheck?: () => void
  rateCheck?: ReactNode
}) {
  const { customer, shipment, payment, vehicles, locations } = forms
  return (
    <div className="grid w-full grid-cols-1 gap-5 lg:grid-cols-2">
      <aside className="flex min-w-0 flex-col gap-5">
        <VehicleSectionCard form={vehicles} />
        <QuoteRouteSectionCard form={locations} />
        <QuoteShipmentCard
          shipmentControl={shipment.control}
          shipmentRegister={shipment.register}
          shipmentErrors={shipment.errors}
          paymentControl={payment.control}
          paymentErrors={payment.errors}
          onPriceCheck={onPriceCheck}
          rateCheck={rateCheck}
        />
      </aside>
      <main className="flex min-w-0 flex-col gap-5">
        <CustomerCard
          control={customer.control}
          register={customer.register}
          setValue={customer.setValue}
          errors={customer.errors}
          searchPlaceholders
          onCustomerSelect={(picked) => onCustomerSelect(picked.id)}
          customerSavedField={customerSavedField}
          onSaveAsNewCustomer={onSaveAsNewCustomer}
          onClearNewCustomer={onClearNewCustomer}
        />
        <PreviewRoadAccordion control={locations.control} />
      </main>
    </div>
  )
}

function OrderFormColumns({
  stage,
  forms,
  onSameAsCustomer,
  onCustomerSelect,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
  onPriceCheck,
  rateCheck,
  tagSeedOptions,
  flags,
}: {
  stage: ShipmentStage
  forms: OrderForms
  onSameAsCustomer?: ReturnType<typeof useSameAsCustomer>
  onCustomerSelect: (customerId: string) => void
  customerSavedField: CustomerSavedField | null
  onSaveAsNewCustomer: (field: CustomerSavedField) => void
  onClearNewCustomer: () => void
  onPriceCheck?: () => void
  rateCheck?: ReactNode
  tagSeedOptions?: MultiSelectOption[]
  flags: UseLocationFlagsResult
}) {
  if (isQuoteLikeStage(stage)) {
    return (
      <QuoteStageColumns
        forms={forms}
        onCustomerSelect={onCustomerSelect}
        customerSavedField={customerSavedField}
        onSaveAsNewCustomer={onSaveAsNewCustomer}
        onClearNewCustomer={onClearNewCustomer}
        onPriceCheck={onPriceCheck}
        rateCheck={rateCheck}
      />
    )
  }
  return (
    <OrderStageColumns
      forms={forms}
      onSameAsCustomer={onSameAsCustomer}
      onCustomerSelect={onCustomerSelect}
      customerSavedField={customerSavedField}
      onSaveAsNewCustomer={onSaveAsNewCustomer}
      onClearNewCustomer={onClearNewCustomer}
      onPriceCheck={onPriceCheck}
      rateCheck={rateCheck}
      tagSeedOptions={tagSeedOptions}
      flags={flags}
    />
  )
}

const FORM_TYPE_BY_STAGE: Partial<Record<ShipmentStage, FormType>> = {
  [SHIPMENT_STAGES.ORDER]: FormType.order,
  [SHIPMENT_STAGES.QUOTE]: FormType.quote,
}

export function OrderForm({
  stage = SHIPMENT_STAGES.ORDER,
  variant = ORDER_FORM_VARIANT.edit,
  defaultValues,
  isSubmitting: externalSubmitting,
  topSection,
  shipmentId,
  onCancel,
  onSubmit,
}: OrderFormProps) {
  const requireDates = stage === SHIPMENT_STAGES.ORDER
  const forms = useOrderForms(defaultValues, requireDates)
  useFormPreferenceSync(FORM_TYPE_BY_STAGE[stage])
  const [submitting, setSubmitting] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>()
  const [customerSavedField, setCustomerSavedField] =
    useState<CustomerSavedField | null>(null)
  const onSaveAsNewCustomer = (field: CustomerSavedField) => {
    setSelectedCustomerId(undefined)
    setCustomerSavedField(field)
  }
  const onClearNewCustomer = () => {
    setCustomerSavedField(null)
  }
  const onCustomerPicked = (id: string) => {
    setSelectedCustomerId(id)
    setCustomerSavedField(null)
  }
  const onSameAsCustomer = useSameAsCustomer(
    selectedCustomerId,
    forms.locations.setValue,
  )
  const flags = useLocationFlags()
  const saveTerminals = useSaveNewTerminals()
  const saveTags = useSaveNewTags()
  const inFlight = submitting || (externalSubmitting ?? false)
  const isQuote = isQuoteLikeStage(stage)
  const priceCheck = usePriceCheck({
    shipmentId,
    getLocation: () => forms.locations.getValues(),
    getVehicles: () => forms.vehicles.getValues(),
    getShipment: () => forms.shipment.getValues(),
    validateLocation: () => forms.locations.trigger(),
    validateVehicles: () => forms.vehicles.trigger(),
    applyTariff: (formattedTariff) => {
      forms.payment.setValue('totalTariff', formattedTariff, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
  })

  const isDirty =
    forms.customer.isDirty ||
    forms.shipment.isDirty ||
    forms.payment.isDirty ||
    forms.vehicles.isDirty ||
    forms.locations.isDirty
  const guard = useUnsavedChangesGuard({ isDirty, isSubmitting: inFlight })

  const handleSave = async (postTo?: LoadboardChoice) => {
    if (submitting) return
    setSubmitting(true)
    try {
      const payload = await validateAndCollect(forms, selectedCustomerId, stage)
      if (!payload) {
        focusFirstInvalidField()
        return
      }
      if (!isQuote) {
        await saveTerminals.save(payload.location, flags.terminalSaved)
      }
      const resolvedTags = isQuote
        ? []
        : await saveTags.save(payload.shipment.tags)
      const rawPayload: OrderFormPayload = {
        ...payload,
        shipment: { ...payload.shipment, tags: resolvedTags },
        postTo,
      }
      const finalPayload = stripHiddenFields(rawPayload, getHiddenFields())
      guard.bypass()
      await onSubmit?.(finalPayload)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ManageFieldsProvider>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-2.5">
        <FormToolbar
          stage={stage}
          variant={variant}
          onCancel={() => guard.guardCancel(() => onCancel?.())}
          onSave={() => void handleSave()}
          onSaveAndPost={(target) => void handleSave(target)}
          isSubmitting={inFlight}
        />
        {topSection}
        <OrderFormColumns
          stage={stage}
          forms={forms}
          onSameAsCustomer={onSameAsCustomer}
          onCustomerSelect={onCustomerPicked}
          customerSavedField={customerSavedField}
          onSaveAsNewCustomer={onSaveAsNewCustomer}
          onClearNewCustomer={onClearNewCustomer}
          onPriceCheck={() => void priceCheck.onPriceCheck()}
          rateCheck={<MarketRateCheck shipmentId={shipmentId} />}
          tagSeedOptions={defaultValues?.tagSeedOptions}
          flags={flags}
        />
        <MarketRateDialog
          open={priceCheck.open}
          onOpenChange={priceCheck.onOpenChange}
          stats={priceCheck.stats}
          rows={priceCheck.rows}
          isLoading={priceCheck.isLoading}
          errorMessage={priceCheck.errorMessage}
          onUseRate={priceCheck.onUseRate}
        />
        {guard.dialogs}
      </div>
    </ManageFieldsProvider>
  )
}
